import { createSelector } from 'reselect';

import type Rate from '~/types/Rate';

import { type SelectedRates } from '~/reducers/rates/rates.reducer';

import { type ApplicationState } from '../store';
import { getRateObjectIdFromProps } from './props.selectors';

const getRatesStore = (state: ApplicationState) => state.rates;

export const getHiddenRates = createSelector(
  getRatesStore,
  (ratesStore): readonly string[] => ratesStore.hiddenRates,
);

export const getAllRatesData = createSelector(
  getRatesStore,
  (ratesStore): HashMap<string, Rate> => ratesStore.data,
);

export const getRatesData = createSelector(
  getAllRatesData,
  getHiddenRates,
  (allRates, hiddenRates): HashMap<string, Rate> =>
    Object.fromEntries(
      Object.entries(allRates).filter(([rateObjectId]) => !hiddenRates.includes(rateObjectId)),
    ),
);

export const getRatesIdsByShipmentId = createSelector(
  getRatesStore,
  getRatesData,
  (ratesStore, activeRates): HashMap<string, readonly string[]> => {
    const ratesIdsByShipmentId = {};
    Object.entries(ratesStore.byShipmentId).forEach(([key, rateIds]) => {
      // filter out rates with service levels that have been hidden by the user
      ratesIdsByShipmentId[key] = rateIds.filter((rateId) => activeRates[rateId]);
    });
    return ratesIdsByShipmentId;
  },
);

// Includes hidden rates (does not filter them out, unlike getRatesIdsByShipmentId)
export const getRawRatesIdsByShipmentId = createSelector(
  getRatesStore,
  (ratesStore): Record<string, readonly string[]> => ratesStore?.byShipmentId ?? {},
);

export const getSelectedRates = createSelector(
  getRatesStore,
  getRatesData,
  (ratesStore, activeRates): SelectedRates => {
    const selectedRateIds = {};
    Object.entries(ratesStore.selectedRates).forEach(([key, rateId]) => {
      // filter out rates with service levels that have been hidden by the user
      if (activeRates[rateId]) selectedRateIds[key] = rateId;
    });
    return selectedRateIds;
  },
);

export const getUserTouchedRates = createSelector(
  getRatesStore,
  (ratesStore) => ratesStore.userTouchedRates,
);

export const getSelectedRatesData = createSelector(
  getRatesData,
  getSelectedRates,
  (activeRates, batchSelectedRates): Rate[] =>
    // filter out rates with service levels that have been hidden by the user
    Object.values(batchSelectedRates)
      .map((selectedRate) => activeRates[selectedRate])
      .filter((rate) => rate),
);

export const getRateByObjectId = createSelector(
  getRatesStore,
  getRateObjectIdFromProps,
  (ratesStore, rateObjectId): Maybe<Rate> => ratesStore.data[rateObjectId] || null,
);
